// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * Pharmacy-facing Reports (D-RPT-2 phase 4). Pick a report, Run it, then filter / search / sort /
 * group / date-range across the WHOLE dataset — all applied SERVER-SIDE and paged, so the browser
 * only ever holds one page. Export CSV pulls the complete set. The last run is cached per tab so it
 * survives leaving the screen and coming back.
 */
import { useState, useEffect } from 'react'
import { FileBarChart, Play, AlertTriangle } from 'lucide-react'
import * as api from '../../api/client'
import ReportResultTable, { ReportServerQuery } from '../../components/common/ReportResultTable'

const PAGE_SIZE = 100

export default function PharmacyReportsPage() {
  const [reports, setReports] = useState<api.MyPublishedReport[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<api.MyPublishedReport | null>(null)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<api.PharmacyReportRunResponse | null>(null)
  const [baseColumns, setBaseColumns] = useState<string[]>([])
  const [numericColumns, setNumericColumns] = useState<string[]>([])
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    api.myPublishedReports().then(r => setReports(r.items ?? [])).catch(e => setError(friendly(e))).finally(() => setLoading(false))
    const c = loadCache()
    if (c) { setSelected(c.sel); setResult(c.result); setBaseColumns(c.baseColumns); setNumericColumns(c.numericColumns) }
  }, [])

  async function fetchPage(rep: api.MyPublishedReport, q: api.ReportRunQuery, initial: boolean) {
    setRunning(true); setError(null)
    try {
      const r = await api.runMyPublishedReport(rep.id, q)
      setResult(r)
      if (r.total_count != null) setTotalCount(r.total_count)
      let base = baseColumns, nums = numericColumns
      if (initial) {
        base = r.columns
        nums = r.columns.filter((_, i) => {
          let seen = 0, num = 0
          for (const row of r.rows) { const v = row[i]; if (v == null || String(v).trim() === '') continue; seen++; if (Number.isFinite(Number(v))) num++; if (seen >= 25) break }
          return seen > 0 && num === seen
        })
        setBaseColumns(base); setNumericColumns(nums)
      }
      saveCache(rep, r, base, nums)
    } catch (e: any) { setError(friendly(e)) } finally { setRunning(false) }
  }

  function runReport(rep: api.MyPublishedReport) {
    setSelected(rep); setResult(null); setTotalCount(null)
    fetchPage(rep, { page: 0, pageSize: PAGE_SIZE, wantCount: true }, true)
  }

  function onQuery(q: ReportServerQuery) {
    if (!selected) return
    fetchPage(selected, {
      page: q.page, pageSize: q.pageSize,
      allColumns: baseColumns,
      search: q.search || undefined,
      filters: q.filters.length ? q.filters : undefined,
      dateCol: q.dateCol, from: q.from || undefined, to: q.to || undefined,
      sortCol: q.sortCol, sortDesc: q.sortDesc,
      groupBy: q.groupBy,
      sumCols: q.groupBy ? numericColumns : undefined,
      wantCount: q.wantCount,
    }, false)
  }

  async function exportCsv() {
    if (!selected) return
    setExporting(true); setError(null)
    try { const { blob, filename } = await api.exportMyPublishedReportCsv(selected.id); saveBlob(blob, filename) }
    catch (e: any) { setError(friendly(e)) } finally { setExporting(false) }
  }

  return (
    <div style={{ padding: '18px 22px', maxWidth: 'var(--page-max, 1200px)', margin: '0 auto' }}>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 21, margin: '0 0 4px' }}>
        <FileBarChart size={19} aria-hidden="true" /> Reports
      </h1>
      <p style={{ margin: '0 0 16px', color: 'var(--color-text-mute)', fontSize: 13 }}>
        Run a report, then filter / group / sort across the whole dataset — paged from the server — and export the complete set.
      </p>

      {error && <div style={errBox}><AlertTriangle size={15} aria-hidden="true" style={{ flexShrink: 0 }} /> {error}</div>}

      {loading ? (
        <p style={{ fontSize: 13, color: 'var(--color-text-mute)' }}>Loading…</p>
      ) : reports.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--color-text-mute)' }}>No reports have been published to your pharmacy yet.</p>
      ) : (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
          {reports.map(rep => (
            <div key={rep.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 12px', borderTop: '1px solid var(--color-border)' }}>
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>{rep.name}</span>
              <button onClick={() => runReport(rep)} disabled={running} style={primaryBtn}>
                <Play size={14} aria-hidden="true" /> {running && selected?.id === rep.id ? 'Running…' : 'Run'}
              </button>
            </div>
          ))}
        </div>
      )}

      {result && selected && (
        <section style={{ marginTop: 20 }}>
          <ReportResultTable
            columns={result.columns}
            rows={result.rows as unknown[][]}
            summary={<span><strong>{selected.name}</strong> <span style={{ color: 'var(--color-text-mute)' }}>· {result.elapsed_ms} ms{totalCount != null ? ` · ${totalCount.toLocaleString()} records` : ''}</span></span>}
            onExport={exportCsv}
            exporting={exporting}
            podHref={(shipmentId) => `shipments?shipment=${encodeURIComponent(shipmentId)}`}
            server={{ page: result.page, pageSize: result.page_size, hasMore: result.has_more, busy: running, baseColumns, totalCount, onQuery, onDistinct: (col, search) => api.reportDistinctValues(selected.id, col, search) }}
          />
        </section>
      )}
    </div>
  )
}

function friendly(e: any): string {
  const body = e?.body
  if (body?.error === 'pharmacy_context_required') return 'Sign in to your pharmacy to view its reports.'
  if (body?.error === 'not_found') return 'That report is no longer available.'
  if (body?.error === 'query_timeout' || body?.error === 'too_expensive' || body?.error === 'sql_error' || body?.error === 'invalid_sql') return body.message
  return body?.message || e?.message || 'Something went wrong.'
}

const CACHE_KEY = 'owlrx_pharmacy_last_report'
function loadCache(): { sel: api.MyPublishedReport; result: api.PharmacyReportRunResponse; baseColumns: string[]; numericColumns: string[] } | null {
  try { const s = sessionStorage.getItem(CACHE_KEY); return s ? JSON.parse(s) : null } catch { return null }
}
function saveCache(sel: api.MyPublishedReport, result: api.PharmacyReportRunResponse, baseColumns: string[], numericColumns: string[]) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ sel, result, baseColumns, numericColumns })) } catch { /* quota/unavailable — best effort */ }
}
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url)
}

const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: 'none', borderRadius: 7, background: 'var(--color-primary, #0d9488)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const errBox: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12, padding: '9px 12px', border: '1px solid var(--color-danger-border, #f0c9c9)', background: 'var(--color-danger-bg, #fdf2f2)', borderRadius: 7, fontSize: 12.5, color: 'var(--color-danger, #b3261e)' }
